import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import LegalDocumentDrawer from './LegalDocumentDrawer';

type LegalDocumentViewerContextValue = {
  openDocument: (slug: string) => void;
};

const LegalDocumentViewerContext = createContext<LegalDocumentViewerContextValue | null>(null);

export const LegalDocumentViewerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [slug, setSlug] = useState<string | null>(null);
  const openDocument = useCallback((nextSlug: string) => setSlug(nextSlug), []);
  const closeDocument = useCallback(() => setSlug(null), []);
  const value = useMemo(() => ({ openDocument }), [openDocument]);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setSlug(null);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  return (
    <LegalDocumentViewerContext.Provider value={value}>
      {children}
      <LegalDocumentDrawer slug={slug} open={!!slug} onClose={closeDocument} />
    </LegalDocumentViewerContext.Provider>
  );
};

export function useLegalDocumentViewer(): LegalDocumentViewerContextValue {
  const ctx = useContext(LegalDocumentViewerContext);
  if (!ctx) {
    throw new Error('useLegalDocumentViewer must be used within LegalDocumentViewerProvider');
  }
  return ctx;
}

type LegalDocumentLinkProps = {
  slug: string;
  className?: string;
  children: React.ReactNode;
};

/** 点击后在弹窗（Drawer）中阅读法律文档，不跳转新标签页 */
export const LegalDocumentLink: React.FC<LegalDocumentLinkProps> = ({ slug, className, children }) => {
  const { openDocument } = useLegalDocumentViewer();
  return (
    <a
      href={`#legal-${slug}`}
      className={className}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        openDocument(slug);
      }}
    >
      {children}
    </a>
  );
};
